import { useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { RateInput, CurrencyInput } from './RateInput'
import NGNIcon from '../../assets/nigeria.svg'
import { Button } from '@/components/ui/button'

interface FeeRange {
    id: string
    from: string
    to: string
    currency: string
    fee: string
    feeUnit: string
    maxFee?: string
    maxFeeCurrency?: string
}

interface TransactionType {
    title: string
    ranges: FeeRange[]
}

export const TransactionChargesTab = () => {
    const [transactionTypes, setTransactionTypes] = useState<
        Record<'buy' | 'sell' | 'send', TransactionType>
    >({
        buy: {
            title: 'Buy',
            ranges: [
                {
                    id: '1',
                    from: '',
                    to: '',
                    currency: 'USD',
                    fee: '0.0',
                    feeUnit: '%',
                },
            ],
        },
        sell: {
            title: 'Sell',
            ranges: [
                {
                    id: '1',
                    from: '',
                    to: '',
                    currency: 'USD',
                    fee: '0.0',
                    feeUnit: '%',
                },
            ],
        },
        send: {
            title: 'Send',
            ranges: [
                {
                    id: '1',
                    from: '',
                    to: '',
                    currency: 'NGN',
                    fee: '0.0',
                    feeUnit: '%',
                },
            ],
        },
    })

    const addRange = (type: 'buy' | 'sell' | 'send') => {
        setTransactionTypes(prev => ({
            ...prev,
            [type]: {
                ...prev[type],
                ranges: [
                    ...prev[type].ranges,
                    {
                        id: Date.now().toString(),
                        from: '',
                        to: '',
                        currency: type === 'send' ? 'NGN' : 'USD',
                        fee: '0.0',
                        feeUnit: '%',
                    },
                ],
            },
        }))
    }

    const removeRange = (type: 'buy' | 'sell' | 'send', id: string) => {
        setTransactionTypes(prev => ({
            ...prev,
            [type]: {
                ...prev[type],
                ranges: prev[type].ranges.filter(range => range.id !== id),
            },
        }))
    }

    const handleRangeChange = (
        type: 'buy' | 'sell' | 'send',
        id: string,
        field: keyof FeeRange,
        value: string
    ) => {
        setTransactionTypes(prev => ({
            ...prev,
            [type]: {
                ...prev[type],
                ranges: prev[type].ranges.map(range =>
                    range.id === id ? { ...range, [field]: value } : range
                ),
            },
        }))
    }

    const addMaximum = (type: 'buy' | 'sell' | 'send', id: string) => {
        setTransactionTypes(prev => ({
            ...prev,
            [type]: {
                ...prev[type],
                ranges: prev[type].ranges.map(range =>
                    range.id === id
                        ? {
                              ...range,
                              maxFee: '0.00',
                              maxFeeCurrency: type === 'send' ? 'NGN' : 'USD',
                          }
                        : range
                ),
            },
        }))
    }

    const renderTransactionSection = (type: 'buy' | 'sell' | 'send') => {
        const section = transactionTypes[type]
        const currencyIcon = type === 'send' ? NGNIcon : NGNIcon
        const currencyCode = type === 'send' ? 'NGN' : 'NGN'

        return (
            <div className="space-y-4">
                <div className="flex h-8 items-center rounded-full bg-[#EDFBFD] px-6">
                    <h3 className="text-sm font-medium text-foreground">
                        {section.title}
                    </h3>
                </div>

                <div className="space-y-6">
                    {section.ranges.map(range => (
                        <div key={range.id} className="space-y-2">
                            <div className="grid gap-4 lg:grid-cols-[1fr_1fr_auto_auto] items-end">
                                {/* Amount Range */}
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-foreground">
                                        Amount range
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <RateInput
                                            placeholder="From"
                                            value={range.from}
                                            onChange={e =>
                                                handleRangeChange(
                                                    type,
                                                    range.id,
                                                    'from',
                                                    e.target.value
                                                )
                                            }
                                        />
                                        <RateInput
                                            placeholder="To"
                                            value={range.to}
                                            onChange={e =>
                                                handleRangeChange(
                                                    type,
                                                    range.id,
                                                    'to',
                                                    e.target.value
                                                )
                                            }
                                        />
                                        <div className="flex h-11 items-center gap-2 rounded-[12px] bg-secondary px-4">
                                            <img
                                                alt={currencyCode}
                                                src={currencyIcon}
                                            />
                                            <span className="text-sm font-medium text-foreground">
                                                {currencyCode}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Fee */}
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-foreground">
                                        Fee
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <CurrencyInput
                                            placeholder="0.0"
                                            value={range.fee}
                                            onChange={e =>
                                                handleRangeChange(
                                                    type,
                                                    range.id,
                                                    'fee',
                                                    e.target.value
                                                )
                                            }
                                            showPercentage
                                        />
                                    </div>
                                </div>

                                {/* Max Fee (if added) */}
                                {range.maxFee !== undefined ? (
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-foreground">
                                            Max fee
                                        </label>
                                        <div className="flex items-center gap-2">
                                            <RateInput
                                                placeholder="0.00"
                                                value={range.maxFee}
                                                onChange={e =>
                                                    handleRangeChange(
                                                        type,
                                                        range.id,
                                                        'maxFee',
                                                        e.target.value
                                                    )
                                                }
                                            />
                                            <div className="flex h-11 items-center gap-2 rounded-[12px] bg-secondary px-4">
                                                <img
                                                    alt={currencyCode}
                                                    src={currencyIcon}
                                                />
                                                <span className="text-sm font-medium text-foreground">
                                                    {currencyCode}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="pb-2">
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            onClick={() =>
                                                addMaximum(type, range.id)
                                            }
                                            className="h-auto gap-2 p-0 text-sm text-[#5FA8B4] hover:bg-transparent hover:text-[#5FA8B4]/80"
                                        >
                                            <Plus className="h-4 w-4" />
                                            Add Maximum
                                        </Button>
                                    </div>
                                )}

                                {/* Delete Button */}
                                {range.maxFee !== undefined && (
                                    <div className="pb-2">
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            onClick={() =>
                                                removeRange(type, range.id)
                                            }
                                            className="h-11 w-11 rounded-[12px] bg-[#FFE8E8] text-[#E53E3E] hover:bg-[#FFE8E8]/80 hover:text-[#E53E3E]"
                                        >
                                            <Trash2 className="h-5 w-5" />
                                        </Button>
                                    </div>
                                )}
                            </div>

                            {/* Delete text for ranges without max fee */}
                            {range.maxFee === undefined &&
                                section.ranges.length > 1 && (
                                    <div className="flex justify-end">
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            onClick={() =>
                                                removeRange(type, range.id)
                                            }
                                            className="h-auto gap-2 p-0 text-sm text-[#E53E3E] hover:bg-transparent hover:text-[#E53E3E]/80"
                                        >
                                            Delete
                                        </Button>
                                    </div>
                                )}
                        </div>
                    ))}

                    <Button
                        type="button"
                        variant="ghost"
                        onClick={() => addRange(type)}
                        className="h-auto gap-2 p-0 text-sm text-[#5FA8B4] hover:bg-transparent hover:text-[#5FA8B4]/80"
                    >
                        <Plus className="h-4 w-4" />
                        Add range
                    </Button>
                </div>
            </div>
        )
    }

    return (
        <div className="space-y-8">
            {/* Transaction Charges Header */}
            <div className="flex h-8 items-center rounded-full bg-[#5FA8B4] px-6">
                <h2 className="text-base font-medium text-white">
                    Transaction charges
                </h2>
            </div>

            {renderTransactionSection('buy')}
            {renderTransactionSection('sell')}
            {renderTransactionSection('send')}
        </div>
    )
}
